import { getStorage, ref, uploadBytes } from "firebase/storage";
import { FirestoreService } from "../api/firebase/firestoreService";

interface FileUploadParams {
  file: File;
  project: string;
  bucket: string;
  blob: string;
  url: string;
}

export const uploadFile = async ({
  file,
  project,
  bucket,
  blob,
  url,
}: FileUploadParams): Promise<Response> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("project", project);
  formData.append("bucket", bucket);
  formData.append("blob", blob);

  return fetch(url, {
    method: "POST",
    headers: {
      accept: "application/json",
    },
    body: formData,
  });
};

/**
 * Converts a Blob to a File object
 */
const blobToFile = (blob: Blob, fileName: string): File => {
  return new File([blob], fileName, { type: blob.type });
};

interface UploadToFirebaseStorageParams {
  file: Blob | File;
  storagePath: string;
  fileName: string;
}

interface UploadAndUpdateFirestoreParams extends UploadToFirebaseStorageParams {
  collection: string;
  documentId: string;
  fieldPath: string;
}

/**
 * Uploads a file to Firebase Storage
 * @param params - Upload parameters
 * @returns The storage path where the file was uploaded
 */
export const uploadToFirebaseStorage = async ({
  file,
  storagePath,
  fileName,
}: UploadToFirebaseStorageParams): Promise<string> => {
  const storage = getStorage();
  const storageRef = ref(storage, storagePath);
  const fileToUpload = file instanceof File ? file : blobToFile(file, fileName);
  await uploadBytes(storageRef, fileToUpload);
  return storagePath;
};

/**
 * Uploads a file to Firebase Storage and updates a Firestore document with the file path
 * @param params - Upload and update parameters
 * @returns The storage path where the file was uploaded
 */
export const uploadToStorageAndUpdateFirestore = async ({
  file,
  storagePath,
  fileName,
  collection,
  documentId,
  fieldPath,
}: UploadAndUpdateFirestoreParams): Promise<string> => {
  await uploadToFirebaseStorage({ file, storagePath, fileName });

  await FirestoreService.updateDocument(collection, documentId, {
    [fieldPath]: storagePath,
  });

  return storagePath;
};
